import moment from 'moment';
import type { CandidateTimeSlot } from './candidate_times';

export const canAssumeMobileDevice = (): boolean => {
  return window.innerWidth <= 500;
};

/**
 * Check to see if two time slots overlap in some way. It ignores any differences in
 * milliseconds when making the calculations
 * */
export const doTimeSlotsOverlap = (
  slot1: CandidateTimeSlot,
  slot2: CandidateTimeSlot
) => {
  let earlierSlot, laterSlot;

  // find the earliest of the two time slots
  if (slot1.start.isBefore(slot2.start, 'minute')) {
    earlierSlot = slot1;
    laterSlot = slot2;
  } else {
    earlierSlot = slot2;
    laterSlot = slot1;
  }

  // next find the smallest time slot by duration
  const slot1Duration = slot1.end.diff(slot1.start);
  const slot2Duration = slot2.end.diff(slot2.start);
  const smallestDuration =
    slot1Duration < slot2Duration ? slot1Duration : slot2Duration;

  // now the way to find out if it overlaps is to take the earliest time and add the duration.
  // if that new time is in between the other time slot, then we know there's overlap
  return moment(earlierSlot.start)
    .add(smallestDuration, 'ms')
    .isBetween(laterSlot.start, laterSlot.end);
};

/**
 * Returns true if a time range specified is already included in the list (when NOT taking
 * into account second and millisecond fields)
 * */
export const isSimilarTimeRangeAlreadyAdded = (
  dateTimeRanges: Array<CandidateTimeSlot>,
  dateTimeRangeToAdd: CandidateTimeSlot
): boolean => {
  const duplicates = dateTimeRanges.filter((range) => {
    return (
      range.start.isSame(dateTimeRangeToAdd.start, 'minute') &&
      range.end.isSame(dateTimeRangeToAdd.end, 'minute')
    );
  });

  if (duplicates.length === 0) {
    return false;
  }
  return true;
};

/**
 * WARNING: This ASSUMES that these time slots overlap. It doesn't actually check before
 *          proceeding.
 *
 *          To actually confirm that they indeed overlap use {#doTimeSlotsOverlap}
 * */
const mergeTimeSlots = (
  slot1: CandidateTimeSlot,
  slot2: CandidateTimeSlot
): CandidateTimeSlot => {
  const earlierDate = slot1.start.isBefore(slot2.start)
    ? slot1.start
    : slot2.start;
  const laterDate = slot1.end.isAfter(slot2.end) ? slot1.end : slot2.end;

  // return a copy (as to not screw up source data by doing any operations on returned object
  return {
    start: moment(earlierDate),
    end: moment(laterDate),
  };
};

export const mergeAndSimplifyTimeSlots = (
  dateTimeRanges: Array<CandidateTimeSlot>,
  mergedSlots: Array<CandidateTimeSlot> = []
): Array<CandidateTimeSlot> => {
  let idx = 0;
  while (dateTimeRanges.length > 0) {
    for (let i = 0; i < mergedSlots.length; i++) {
      if (doTimeSlotsOverlap(dateTimeRanges[idx], mergedSlots[i])) {
        // update value in the merged time slots
        mergedSlots[i] = mergeTimeSlots(dateTimeRanges[idx], mergedSlots[i]);

        // now that the merged array has been updated, remove it from date time ranges
        dateTimeRanges.splice(idx, 1);
      }
    }
    // there were no overlapping time slots so just move that into the merged slots array
    // provided that we didn't already do that in the last iteration of the loop
    if (dateTimeRanges.length > 0) {
      mergedSlots.push(dateTimeRanges[idx]);
      dateTimeRanges.splice(idx, 1);
    }
  }

  return mergedSlots;
};
